/**
 * Peer Metrics Service — Singleton multiplexed worker for all peers
 * 
 * R5 FIX: Consolidated from per-peer instantiation to a single shared worker.
 * Previously each MobyPeer created its own PeerMetricsService + dedicated worker,
 * causing 12+ workers at 12 peers → OOM crash. Now one worker maintains per-peer
 * state via Map<peerId, PeerState>, keyed by peer address.
 * 
 * Usage:
 *   import { peerMetricsService } from '@/services/peer-metrics-service';
 *   await peerMetricsService.init();
 *   peerMetricsService.register(peerAddress);
 *   peerMetricsService.onMetricsUpdate(peerAddress, (metrics) => { ... });
 *   peerMetricsService.postConnectionStats(peerAddress, { rtt, jitter, ... });
 *   peerMetricsService.unregister(peerAddress);
 * 
 * Design doc: docs/peer-metrics-worker-design.md
 * Fix doc: docs/cross-component-conflict.md §12.4 R5, §12.5 Phase 1
 */

// ─── Types ──────────────────────────────────────────────────────────────────

export type HealthScore = 'excellent' | 'good' | 'fair' | 'poor' | 'unknown';
export type DbHealthScore = 'green' | 'yellow' | 'red' | 'gray';

export interface MetricsUpdate {
  type: 'metrics-update';
  quality: HealthScore;
  bandwidth: number;
  totalBandwidth: number;
  rtt: number;
  jitter: number;
  bufferThreshold: number;
  dbHealthScore: DbHealthScore;
  activeTransferCount: number;
}

export interface HealthUpdate {
  type: 'health-update';
  healthScore: DbHealthScore;
  quality: HealthScore;
  rtt: number;
  jitter: number;
  packetsLost: number;
  packetsSent: number;
  bytesSent: number;
  bytesReceived: number;
  lastStatsUpdate: number;
}

export interface ConnectionStatsMessage {
  rtt: number;
  jitter: number;
  packetsLost: number;
  packetsSent: number;
  bytesSent: number;
  bytesReceived: number;
  lastStatsUpdate: number;
}

type MetricsUpdateCallback = (metrics: MetricsUpdate) => void;
type HealthUpdateCallback = (health: HealthUpdate) => void;

type PendingRequest = {
  resolve: (value: any) => void;
  reject: (error: Error) => void;
};

// ─── Per-Peer Callback/Cache State (main-thread side) ────────────────────────

interface PeerCallbackState {
  metricsCallbacks: MetricsUpdateCallback[];
  healthCallbacks: HealthUpdateCallback[];
  latestMetrics: MetricsUpdate | null;
  latestHealth: HealthUpdate | null;
  pendingBandwidthBytes: number;
  bandwidthFlushTimer: ReturnType<typeof setTimeout> | null;
}

function createPeerCallbackState(): PeerCallbackState {
  return {
    metricsCallbacks: [],
    healthCallbacks: [],
    latestMetrics: null,
    latestHealth: null,
    pendingBandwidthBytes: 0,
    bandwidthFlushTimer: null,
  };
}

// ─── Singleton Service ──────────────────────────────────────────────────────

class PeerMetricsService {
  private worker: Worker | null = null;
  private workerAvailable = false;
  private initPromise: Promise<void> | null = null;
  
  // Per-peer callback/cache state (main-thread side)
  private peerStates = new Map<string, PeerCallbackState>();
  
  // Request/response for get-metrics
  private pendingRequests = new Map<number, PendingRequest>();
  private nextRequestId = 0;

  private readonly BANDWIDTH_FLUSH_INTERVAL = 200; // Flush every 200ms max

  // ─── Initialization ─────────────────────────────────────────────────────

  async init(): Promise<void> {
    if (this.initPromise) return this.initPromise;
    this.initPromise = this._doInit();
    return this.initPromise;
  }

  private async _doInit(): Promise<void> {
    if (typeof Worker === 'undefined') {
      console.warn('[PeerMetricsService] Web Workers not supported, using main-thread fallback');
      return;
    }

    try {
      await this.spawnWorker();
      console.log('[PeerMetricsService] Shared metrics worker initialized successfully');
    } catch (e) {
      console.warn('[PeerMetricsService] Worker spawn failed, using main-thread fallback', e);
    }
  }

  private spawnWorker(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('Worker init timed out'));
      }, 5000);

      try {
        this.worker = new Worker('/peer-metrics-worker.js', { type: 'module' });

        this.worker.onmessage = (e: MessageEvent) => {
          this.handleMessage(e);
        };

        this.worker.onerror = (e: ErrorEvent) => {
          console.warn('[PeerMetricsService] Worker error:', e.message);
          clearTimeout(timeout);
          this.teardownWorker();
          reject(new Error(e.message));
        };

        // Worker is ready immediately (no WASM init needed)
        clearTimeout(timeout);
        this.workerAvailable = true;
        resolve();
      } catch (e) {
        clearTimeout(timeout);
        reject(e);
      }
    });
  }

  private teardownWorker(): void {
    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
    }
    this.workerAvailable = false;
  }

  // ─── Peer Lifecycle ─────────────────────────────────────────────────────

  /** Register a peer — creates per-peer state in worker and main-thread callback map */
  register(peerId: string): void {
    if (!this.peerStates.has(peerId)) {
      this.peerStates.set(peerId, createPeerCallbackState());
    }
    if (this.workerAvailable && this.worker) {
      this.worker.postMessage({ type: 'register', peerId });
    }
  }

  /** Unregister a peer — cleans up worker state and main-thread callbacks */
  unregister(peerId: string): void {
    const ps = this.peerStates.get(peerId);
    if (ps) {
      if (ps.bandwidthFlushTimer) {
        clearTimeout(ps.bandwidthFlushTimer);
      }
      this.peerStates.delete(peerId);
    }
    if (this.workerAvailable && this.worker) {
      this.worker.postMessage({ type: 'unregister', peerId });
    }
  }

  /** Check if a specific peer is registered */
  isPeerRegistered(peerId: string): boolean {
    return this.peerStates.has(peerId);
  }

  // ─── Message Handling ───────────────────────────────────────────────────

  private handleMessage(e: MessageEvent): void {
    const msg = e.data;
    const peerId: string | undefined = msg.peerId;

    switch (msg.type) {
      case 'metrics-update': {
        if (!peerId) break;
        const ps = this.peerStates.get(peerId);
        if (!ps) break;
        const update = msg as MetricsUpdate;
        ps.latestMetrics = update;
        for (const cb of ps.metricsCallbacks) {
          try { cb(update); } catch { /* callback error */ }
        }
        break;
      }

      case 'health-update': {
        if (!peerId) break;
        const ps = this.peerStates.get(peerId);
        if (!ps) break;
        const update = msg as HealthUpdate;
        ps.latestHealth = update;
        for (const cb of ps.healthCallbacks) {
          try { cb(update); } catch { /* callback error */ }
        }
        break;
      }

      case 'get-metrics-response': {
        const pending = this.pendingRequests.get(msg.id);
        if (pending) {
          this.pendingRequests.delete(msg.id);
          pending.resolve(msg);
        }
        break;
      }
    }
  }

  // ─── Callbacks (per-peer) ──────────────────────────────────────────────

  onMetricsUpdate(peerId: string, cb: MetricsUpdateCallback): () => void {
    const ps = this.peerStates.get(peerId);
    if (!ps) return () => {};
    ps.metricsCallbacks.push(cb);
    return () => {
      ps.metricsCallbacks = ps.metricsCallbacks.filter(c => c !== cb);
    };
  }

  onHealthUpdate(peerId: string, cb: HealthUpdateCallback): () => void {
    const ps = this.peerStates.get(peerId);
    if (!ps) return () => {};
    ps.healthCallbacks.push(cb);
    return () => {
      ps.healthCallbacks = ps.healthCallbacks.filter(c => c !== cb);
    };
  }

  // ─── Inbound Messages (main → worker, per-peer) ───────────────────────

  /**
   * Forward WebRTC connection stats snapshot (called from updateConnectionStats, every 5s)
   */
  postConnectionStats(peerId: string, stats: ConnectionStatsMessage): void {
    if (!this.workerAvailable || !this.worker) return;
    this.worker.postMessage({ type: 'connection-stats', peerId, ...stats });
  }

  /**
   * Track bandwidth bytes from file transfer (batched to reduce postMessage frequency)
   * Called per-symbol on both sender and receiver paths.
   */
  postBandwidthSample(peerId: string, bytes: number): void {
    if (!this.workerAvailable || !this.worker) return;
    const ps = this.peerStates.get(peerId);
    if (!ps) return;
    
    ps.pendingBandwidthBytes += bytes;
    
    // Flush on timer to batch many per-symbol calls into one postMessage
    if (!ps.bandwidthFlushTimer) {
      ps.bandwidthFlushTimer = setTimeout(() => {
        ps.bandwidthFlushTimer = null;
        if (ps.pendingBandwidthBytes > 0 && this.worker) {
          this.worker.postMessage({
            type: 'bandwidth-sample',
            peerId,
            bytes: ps.pendingBandwidthBytes,
            timestamp: Date.now(),
          });
          ps.pendingBandwidthBytes = 0;
        }
      }, this.BANDWIDTH_FLUSH_INTERVAL);
    }
  }

  /**
   * Notify worker of transfer lifecycle events
   */
  postTransferStarted(peerId: string, sessionId: string, fileSize?: number): void {
    if (!this.workerAvailable || !this.worker) return;
    this.worker.postMessage({ type: 'transfer-started', peerId, sessionId, fileSize });
  }

  postTransferCompleted(peerId: string, sessionId: string): void {
    if (!this.workerAvailable || !this.worker) return;
    this.worker.postMessage({ type: 'transfer-completed', peerId, sessionId });
  }

  postTransferFailed(peerId: string, sessionId: string): void {
    if (!this.workerAvailable || !this.worker) return;
    this.worker.postMessage({ type: 'transfer-failed', peerId, sessionId });
  }

  postTransferCancelled(peerId: string, sessionId: string): void {
    if (!this.workerAvailable || !this.worker) return;
    this.worker.postMessage({ type: 'transfer-cancelled', peerId, sessionId });
  }

  postTransferPaused(peerId: string, sessionId: string): void {
    if (!this.workerAvailable || !this.worker) return;
    this.worker.postMessage({ type: 'transfer-paused', peerId, sessionId });
  }

  postTransferResumed(peerId: string, sessionId: string): void {
    if (!this.workerAvailable || !this.worker) return;
    this.worker.postMessage({ type: 'transfer-resumed', peerId, sessionId });
  }

  /**
   * Reset bandwidth tracking for new transfer series
   */
  resetBandwidth(peerId: string): void {
    if (!this.workerAvailable || !this.worker) return;
    const ps = this.peerStates.get(peerId);
    if (ps) ps.pendingBandwidthBytes = 0;
    this.worker.postMessage({ type: 'reset-bandwidth', peerId });
  }

  /**
   * Set active transfer count directly (for queue corrections)
   */
  setActiveCount(peerId: string, count: number): void {
    if (!this.workerAvailable || !this.worker) return;
    this.worker.postMessage({ type: 'set-active-count', peerId, count });
  }

  // ─── Getters (per-peer) ────────────────────────────────────────────────

  /** Get latest cached metrics for a peer (non-blocking) */
  getLatestMetrics(peerId: string): MetricsUpdate | null {
    return this.peerStates.get(peerId)?.latestMetrics ?? null;
  }

  /** Get latest cached health update for a peer (non-blocking) */
  getLatestHealth(peerId: string): HealthUpdate | null {
    return this.peerStates.get(peerId)?.latestHealth ?? null;
  }

  /** Whether the shared worker is active */
  get isWorkerAvailable(): boolean {
    return this.workerAvailable;
  }

  /**
   * Request current metrics for a peer via request/response.
   * Use sparingly — prefer cached getLatestMetrics for UI.
   */
  async getMetrics(peerId: string): Promise<MetricsUpdate | null> {
    const cached = this.peerStates.get(peerId)?.latestMetrics ?? null;
    if (!this.workerAvailable || !this.worker) {
      return cached;
    }

    const id = this.nextRequestId++;
    return new Promise<MetricsUpdate | null>((resolve) => {
      const timeout = setTimeout(() => {
        this.pendingRequests.delete(id);
        resolve(cached); // Fallback to cached
      }, 1000);

      this.pendingRequests.set(id, {
        resolve: (value) => {
          clearTimeout(timeout);
          resolve(value as MetricsUpdate);
        },
        reject: () => {
          clearTimeout(timeout);
          resolve(cached);
        }
      });

      this.worker!.postMessage({ type: 'get-metrics', peerId, id });
    });
  }

  // ─── Cleanup ────────────────────────────────────────────────────────────

  /** Dispose entire service — terminates worker and clears all state */
  dispose(): void {
    for (const ps of this.peerStates.values()) {
      if (ps.bandwidthFlushTimer) {
        clearTimeout(ps.bandwidthFlushTimer);
      }
    }
    this.peerStates.clear();
    if (this.worker) {
      this.worker.postMessage({ type: 'dispose' });
      this.teardownWorker();
    }
    this.pendingRequests.clear();
  }
}

// ─── Singleton Export ────────────────────────────────────────────────────────

export const peerMetricsService = new PeerMetricsService();
